import { LanguageService } from '@app/core/services/language.service';
import { FormDefinitionService } from '@app/core/services/form-definition.service';
import { MiscService } from '@app/core/services/misc.service';
import { ChangeDetectionStrategy, Component, ChangeDetectorRef, inject } from '@angular/core';
import { FieldWrapper, FormlyModule } from '@ngx-formly/core';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { DynamicIconComponent } from "@app/shared/components/dynamic-icon";
import { TooltipDirective } from "@app/core/directives/ToolTips/tooltip.directive";


@Component({
  selector: 'formly-field-pages',
  templateUrl: './formly-field-pages.component.html',
  styleUrls: ['./formly-field-pages.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonicModule,
    FormlyModule,
    TranslateModule,
    DynamicIconComponent,
    TooltipDirective,
],
})
export class FormlyFieldPagesWrapperComponent extends FieldWrapper {
  private formDefinitionService = inject(FormDefinitionService);
  private languageService = inject(LanguageService);
  private miscService = inject(MiscService);

  public visiblePageIndex: number = 0;
  private changeDetectorRef = inject(ChangeDetectorRef);

  constructor() {
    super();
    // Find the first page that isn't hidden by expressions
    this.visiblePageIndex = this.field.fieldGroup?.findIndex((fg: any) => this.isPageVisibleByExpression(fg)) || 0;
  }

  public selectPage(index: number, field: any): void {
    this.visiblePageIndex = index;
    this.changeDetectorRef.markForCheck();
    if(this.options.formState.disabled) {
      setTimeout(() => {
        const element = document.getElementById(`page_${index}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const isNarrowScreen = window.matchMedia('(max-width: 768px)').matches;
      if (isNarrowScreen) {
        this.scrollToPageAnchor(index);
      } else {
        const element = document.getElementById('top-of-page');
        if (element) {
          element.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      }
    }
  }

  private scrollToPageAnchor(index: number): void {
    setTimeout(() => {
      const element = document.getElementById(`page_${index}`);
      if (!element) {
        return;
      }
      const header = document.querySelector('app-header') as HTMLElement | null;
      const nav = document.querySelector('.page-nav') as HTMLElement | null;
      const headerHeight = header?.getBoundingClientRect().height || 0;
      const navHeight = nav?.getBoundingClientRect().height || 0;
      const offset = headerHeight + navHeight + 12;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }, 0);
  }

  public isPageVisibleByExpression(pageField: any): boolean {
    // Respect the field's original hide property/expression
    // This checks if the field is hidden by expressions.hide
    return !pageField.hide;
  }

  public isPageSelected(index: number): boolean {
    return this.visiblePageIndex === index;
  }

  public hasInvalidFieldsInPage(pageField: any): boolean {
    const flattenedFields = this.formDefinitionService.flattenFormFields(pageField.fieldGroup!);
    const res = flattenedFields.some((f: any) => !f.hide && f.formControl && f.formControl.invalid && f.wrappers.includes('BASIC_FIELD'));
    return res;
  }

  public getNamesOfInvalidFieldsInPage(pageField: any): string {
    const flattenedFields = this.formDefinitionService.flattenFormFields(pageField.fieldGroup!);
    const invalidFields = flattenedFields.filter((f: any) => !f.hide && f.formControl && f.formControl.invalid && f.wrappers.includes('BASIC_FIELD'));
    if (invalidFields.length === 0) {
      return '';
    }
    return `${this.languageService.instant('ERRORS.INVALID_FIELDS_LIST')}:\n${invalidFields.map((f: any) => `- ${f.props.label || f.key || 'Unnamed field'}`).join('\n')}`;
  }


  public hasVisibleChildren(pageField: any): boolean {
    const flattenedFields = pageField?.fieldGroup || [];
    return flattenedFields.some((f: any) => !f.hide && f.type !== 'HIDDEN');
  }

  public getVisiblePageEntries(): Array<{ index: number; field: any }> {
    return (this.field.fieldGroup || [])
      .map((field: any, index: number) => ({ index, field }))
      .filter((entry) => this.hasVisibleChildren(entry.field) && this.isPageVisibleByExpression(entry.field));
  }

  public getCurrentPageEntry(): { index: number; field: any } | null {
    const entries = this.getVisiblePageEntries();
    if (entries.length === 0) {
      return null;
    }
    return entries.find((entry) => entry.index === this.visiblePageIndex) || entries[0];
  }

  public getCurrentPagePosition(): number {
    const entries = this.getVisiblePageEntries();
    const currentIndex = entries.findIndex((entry) => entry.index === this.visiblePageIndex);
    if (currentIndex === -1) {
      return entries.length > 0 ? 1 : 0;
    }
    return currentIndex + 1;
  }

  public getTotalVisiblePages(): number {
    return this.getVisiblePageEntries().length;
  }

  public canGoPrevious(): boolean {
    const entries = this.getVisiblePageEntries();
    const currentIndex = entries.findIndex((entry) => entry.index === this.visiblePageIndex);
    return currentIndex > 0;
  }

  public canGoNext(): boolean {
    const entries = this.getVisiblePageEntries();
    const currentIndex = entries.findIndex((entry) => entry.index === this.visiblePageIndex);
    return currentIndex >= 0 && currentIndex < entries.length - 1;
  }

  public goToPreviousPage(): void {
    const entries = this.getVisiblePageEntries();
    const currentIndex = entries.findIndex((entry) => entry.index === this.visiblePageIndex);
    if (currentIndex > 0) {
      const target = entries[currentIndex - 1];
      this.selectPage(target.index, target.field);
    }
  }

  public goToNextPage(): void {
    const entries = this.getVisiblePageEntries();
    const currentIndex = entries.findIndex((entry) => entry.index === this.visiblePageIndex);
    if (currentIndex >= 0 && currentIndex < entries.length - 1) {
      const target = entries[currentIndex + 1];
      this.selectPage(target.index, target.field);
    }
  }
}
